import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  RouterProvider,
  createRouter,
  createRootRoute,
  createRoute,
} from "@tanstack/react-router";
import "./index.css";
import { loadAllFonts } from "./lib/fonts";
import { initTheme } from "./lib/theme";

// Stamp the persisted light/dark preference on <html> before first paint.
initTheme();

// Load the curated Google Fonts registry at app boot so the Remotion Player
// has them available before rendering any user template. The render worker
// (render-entry.tsx) loads the same set, giving both contexts identical
// glyph widths — which is what keeps preview and render visually aligned.
loadAllFonts();

import { DashboardPage } from "./pages/Dashboard";
import { LoginPage } from "./pages/Login";
import { ForgotPasswordPage } from "./pages/ForgotPassword";
import { ResetPasswordPage } from "./pages/ResetPassword";
import { ConfirmEmailChangePage } from "./pages/ConfirmEmailChange";
import { AuthCallbackPage } from "./pages/AuthCallback";
import { CreatePage } from "./pages/Create";
import { TemplatesPage } from "./pages/Templates";
import { TemplateDetailPage } from "./pages/TemplateDetail";
import { RendersPage } from "./pages/Renders";
import { RenderDetailPage } from "./pages/RenderDetail";
import { SettingsPage } from "./pages/Settings";
import { TeamSettingsPage } from "./pages/TeamSettings";
import { CreateTeamPage } from "./pages/CreateTeam";
import { AdminPage } from "./pages/Admin";

// Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Routes
const rootRoute = createRootRoute();

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: DashboardPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
});

const confirmEmailChangeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/confirm-email-change",
  component: ConfirmEmailChangePage,
});

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/callback",
  component: AuthCallbackPage,
});

const createRoute_ = createRoute({
  getParentRoute: () => rootRoute,
  path: "/create",
  component: CreatePage,
});

const templatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/templates",
  component: TemplatesPage,
});

const templateDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/templates/$id",
  component: function TemplateDetailWrapper() {
    const { id } = templateDetailRoute.useParams();
    return <TemplateDetailPage templateId={id} />;
  },
});

const rendersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/renders",
  component: RendersPage,
});

const renderDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/renders/$id",
  component: function RenderDetailWrapper() {
    const { id } = renderDetailRoute.useParams();
    return <RenderDetailPage renderId={id} />;
  },
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: SettingsPage,
});

const teamSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/teams/$id/settings",
  component: function TeamSettingsWrapper() {
    const { id } = teamSettingsRoute.useParams();
    return <TeamSettingsPage teamId={id} />;
  },
});

const createTeamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/teams/new",
  component: CreateTeamPage,
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: AdminPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  confirmEmailChangeRoute,
  authCallbackRoute,
  createRoute_,
  templatesRoute,
  templateDetailRoute,
  rendersRoute,
  renderDetailRoute,
  settingsRoute,
  teamSettingsRoute,
  createTeamRoute,
  adminRoute,
]);

const router = createRouter({ routeTree });

// Type safety for router
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Mount
const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element not found");

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
